import type {
  AIBlockProposal,
  AIEvidenceReviewPayload,
  AIExplanation,
  AIPurpose,
  AITrendAnalysis,
  AIWeeklyReview,
} from '@cadencia/contracts';

/**
 * `CoachAIProvider`: la unica puerta por la que el dominio habla con una IA
 * (docs/AI_ARCHITECTURE.md §4).
 *
 * **El dominio no importa el SDK de Anthropic ni sabe que existe.** Habla con
 * esta interfaz, y cambiar de proveedor —o quitarlo— es escribir otra
 * implementacion y nada mas. No es purismo: es lo que hace cierta la frase de
 * que si manana desapareciera la API, esto seguiria siendo un entrenador
 * funcional.
 *
 * Toda operacion devuelve un `AIResult`, nunca lanza. Una IA que no responde
 * no es una excepcion: es el caso normal para el que hay camino determinista.
 */

/**
 * `AIError` y la auditoria de contexto viven en `@cadencia/contracts` porque
 * viajan por el cable y tienen que ser LA MISMA definicion a los dos lados;
 * se reexportan aqui para quien ya importa del dominio.
 */
export type { AIError } from '@cadencia/contracts';
import type { AIError } from '@cadencia/contracts';

/**
 * `decisionId` es la fila de `ai_decisions` que el servidor creo para esta
 * propuesta, y viaja de vuelta para que el dominio pueda **poner su veredicto**
 * sobre ella. Sin el, `accepted` se quedaria en nulo para siempre y la frase
 * «el dominio manda sobre el modelo» no tendria ni un solo registro que la
 * respaldara.
 *
 * Opcional porque `DisabledCoachAI` y los dobles de test no lo tienen, y
 * porque un veredicto sin fila que anotar no es un error: es una redaccion que
 * nunca llego a guardarse.
 */
export type AIResult<T> =
  | { ok: true; value: T; decisionId?: string | undefined; promptVersion?: string | undefined }
  | { ok: false; error: AIError };

/** `promptVersion`: la que informa el servidor, para registrarla sin suponerla. */
export function aiOk<T>(value: T, decisionId?: string, promptVersion?: string): AIResult<T> {
  return {
    ok: true,
    value,
    ...(decisionId === undefined ? {} : { decisionId }),
    ...(promptVersion === undefined ? {} : { promptVersion }),
  };
}

export function aiFail<T>(error: AIError): AIResult<T> {
  return { ok: false, error };
}

/**
 * Contexto de una explicacion.
 *
 * Lo que se envia son los **codigos de motivo y los numeros de la decision**,
 * nada mas (§7.1 y §10). Ni nombre, ni email, ni identificadores: ver
 * `toAIContext()` en la capa de aplicacion, que es donde se recorta.
 */
export interface ExplanationContext {
  /** Que se decidio, en vocabulario del motor. */
  decisionType: string;
  reasonCodes: readonly string[];
  /** Los numeros que sostienen la decision, ya calculados. */
  facts: Readonly<Record<string, number | string | null>>;
  /** La version determinista, que es la que se usa si la IA falla. */
  deterministicText: string;
}

export interface WeeklyReviewContext {
  weekStart: string;
  metrics: Readonly<Record<string, number | string | null>>;
  reasonCodes: readonly string[];
  /** La decision YA tomada. La IA redacta; no elige. */
  decision: 'maintain' | 'adjust';
  deterministicSummary: string;
  deterministicInterpretation: string;
}

export interface TrendContext {
  /** Serie ya agregada por semanas, nunca los registros diarios (§7.1). */
  weeklySeries: readonly { weekStart: string; value: number }[];
  metric: string;
  /**
   * Senales que apuntan en direcciones distintas.
   *
   * La clave es `signal` y **no puede ser `name`**: `auditAIContext` prohibe
   * esa clave este donde este, porque es la forma clasica de que se escape una
   * identidad. Con `name` este contexto no habria salido nunca del dispositivo
   * —y sin error visible: la peticion se cancela y la pantalla se queda con su
   * texto determinista, que es justo el fallo invisible que la auditoria
   * describe—. Lo encontro el test de contexto el 01/09/2026, antes de que
   * llegara a ejecutarse una sola vez.
   */
  conflictingSignals: readonly { signal: string; direction: string }[];
}

/**
 * Contexto para elegir los ejercicios de UNA sesion.
 *
 * Una sesion por peticion, y no el bloque entero, por dos motivos que apuntan
 * al mismo sitio: el contexto cabe de sobra en el tope del §7.1, y un rechazo
 * afecta a una sesion en vez de tumbar el bloque completo.
 *
 * `candidates` ya viene filtrado por el gimnasio del usuario
 * (`selectableSlots`). Ofrecerle ejercicios que no puede hacer garantiza que a
 * veces los elija, y entonces se rechaza todo por una pregunta mal hecha.
 */

/**
 * Contexto para proponer la estructura del bloque que viene.
 *
 * Lleva **la respuesta del motor determinista dentro**: `defaultWeeks` y, por
 * musculo, las series que da la plantilla y su banda. No es cortesia: sin la
 * base, «sube el volumen del dorsal» no significa nada y el modelo tendria que
 * inventarse un punto de partida, que es exactamente lo que no puede hacer.
 *
 * La fase NO viaja como pregunta, viaja como hecho. La decide `selectBlockPhase`
 * con las señales del §2.3 y no se negocia; el modelo propone dentro de ella.
 */
/**
 * Lo que se le manda para revisar evidencia. **Nada del atleta** (§14).
 *
 * Ni peso, ni historial, ni objetivo, ni prioridades. Una pregunta sobre la
 * literatura y las afirmaciones del vocabulario que se quieren respaldar. Es la
 * unica llamada del sistema que sale a internet, y por eso es la unica donde el
 * contexto no puede ser «anonimo»: tiene que ser impersonal.
 *
 * `auditAIContext` ya rechazaria un `userId`, pero eso protege de una fuga
 * accidental, no de mandar «varon de 34 años que entrena 4 dias» a un buscador
 * bibliografico. De eso protege que aqui no haya donde ponerlo.
 */
export interface EvidenceReviewContext {
  /** La pregunta, en lenguaje natural. */
  question: string;
  /** Afirmaciones del vocabulario que se quieren respaldar o refutar. */
  claims: readonly string[];
  /** Fecha de hoy, para que el modelo pueda acotar «reciente». */
  today: string;
  /**
   * Lo que YA hay en la base para esas afirmaciones, por identificador.
   *
   * No son fichas: son DOI y PMID a secas. Sirven para que no vuelva a traer lo
   * mismo, y mandar mas seria mandar contenido que ya tenemos pagando por el
   * doble.
   */
  known: readonly string[];
}

export interface BlockProposalContext {
  phase: string;
  /** Semanas que la fase tiene tabuladas, y el margen en el que se puede mover. */
  defaultWeeks: number;
  minWeeks: number;
  maxWeeks: number;
  /** Por que el motor eligio esta fase, en codigos de motivo. */
  phaseReasonCodes: readonly string[];
  priorityMuscles: readonly string[];
  /** Tope duro de sesiones de fuerza por semana (`SAFETY_LIMITS`). */
  maxSessionsPerWeek: number;
  /**
   * Sesiones que el atleta declaró poder hacer en el test inicial: el plan
   * tiene que traer EXACTAMENTE estas, y `validateSessionPlan` lo comprueba.
   * `null` = un perfil anterior al test; entonces solo manda el tope.
   *
   * Los días concretos y las limitaciones NO viajan. Los días no cambian el
   * diseño de la semana tipo —lo reparte el calendario— y las limitaciones son
   * un dato de salud que no hace falta que salga del dispositivo: el catálogo
   * ya llega sin lo que vetan (docs/TEST_INICIAL.md §2.3).
   */
  sessionsPerWeek: number | null;
  /** Minutos que una sesion no debe superar. Se comprueba al validar. */
  maxSessionMinutes: number;
  /**
   * Los ejercicios entre los que puede elegir: los del catalogo que ADEMAS
   * caben en el gimnasio del usuario.
   *
   * Es una lista cerrada, y esa es toda la garantia: el §7 del encargo dice que
   * «la IA elegira entre ejercicios validos del catalogo, no inventara
   * identificadores ni equipamiento». Que el esquema admita cualquier cadena no
   * lo contradice —un slug inventado es justo lo que `validateSessionPlan`
   * tiene que poder rechazar— pero dandole la lista, inventarse uno deja de
   * tener sentido para el modelo.
   *
   * VA AL HUESO, Y EL MOTIVO ES CA-7.6: el contexto entero tiene que caber en
   * 4.000 tokens. El catalogo completo son 89 ejercicios, y medido el
   * 22/09/2026 con `estimateContextTokens`:
   *
   * | Que se manda por ejercicio | Tokens del contexto |
   * |---|---|
   * | slug, nameEs, pattern, primaryMuscles, isCompound | 7.195 |
   * | slug, pattern, primaryMuscles, isCompound | 5.756 |
   * | **slug, primaryMuscles** | **3.748** |
   *
   * Solo la ultima cabe. Y no se pierde casi nada, porque los slugs de este
   * catalogo son descriptivos en castellano —`press-banca-mancuernas`,
   * `elevacion-gemelos-sentado`, `jalon-agarre-neutro`—: el nombre, el patron y
   * si es basico o de aislamiento se leen en el propio identificador.
   *
   * La alternativa era subir el tope, y NO se ha hecho. Cuando esto mismo paso
   * con `recipe_generation` el 01/09/2026 —4.755 tokens contra un tope de
   * 4.000— la respuesta fue recortar la lista de alimentos a 40 y dejar el tope
   * donde estaba. Un tope que se sube cada vez que algo no cabe no es un tope.
   *
   * Lo que SI se perderia recortando el numero de ejercicios es capacidad de
   * diseño, que es justo lo que este proposito existe para dar: por eso se
   * recortan los CAMPOS y no las filas.
   *
   * AGRUPADO POR MUSCULOS PRIMARIOS desde el 22/09/2026: la clave es la lista
   * de musculos primarios unida con «+» (`pectoral+deltoide_anterior`) y el
   * valor, sus slugs. Es la misma informacion —cada slug sale una vez, con sus
   * musculos— sin repetir `"slug"` y `"primaryMuscles"` 89 veces. Medido con el
   * catalogo entero: 2.757 tokens como lista, 1.085 agrupado, 23 grupos. Ese
   * margen es el que deja entrar la memoria del atleta y las fichas de
   * evidencia sin tocar el tope.
   */
  catalog: Readonly<Record<string, readonly string[]>>;
  /**
   * Lo observado en ESTE atleta al cambiar el volumen (E5, `memoryForPlanning`).
   * Frases con su nivel en palabras, nunca un numero: el §10 prohibe presentar
   * una puntuacion como probabilidad. Vacio si no hay nada evaluado.
   */
  athleteMemory: readonly { finding: string; confidence: string }[];
  /**
   * Las fichas de evidencia ACTIVAS, numeradas «E1», «E2»… (§14).
   *
   * `ref` y no `id`: el identificador de la fila no sale del dispositivo
   * (`auditAIContext` lo rechaza) y ademas no le sirve de nada al modelo. El
   * cliente guarda la correspondencia y traduce las citas al volver.
   */
  evidence: readonly {
    ref: string;
    claims: readonly string[];
    certainty: string;
    applicability: string;
    /** Los hallazgos, recortados: la ficha entera no cabe en el tope. */
    finding: string;
  }[];
  muscles: readonly {
    muscle: string;
    /** Series semanales que produce la plantilla. El punto de partida. */
    templateSets: number;
    bandMin: number;
    bandTargetMin: number;
    bandTargetMax: number;
    bandMax: number;
  }[];
}

/**
 * Contexto para generar una receta que sustituya UNA comida.
 *
 * `candidateFoods` no es el catalogo entero: son los alimentos con los que el
 * sistema ya cocina en ese hueco, mas la despensa (`candidateFoodsForSlot`).
 * Los 255 del catalogo no caben en el tope del §7.1, y ademas ofrecerlos todos
 * convertiria la pregunta en «inventa algo».
 *
 * El objetivo de macros viaja como HECHO: es lo que esa comida tenia que
 * aportar dentro del dia, y no se negocia. La receta que no lo cumpla se
 * rechaza entera.
 */

export interface CoachAIProvider {
  /** Si esta apagada, todas las llamadas devuelven `disabled` sin salir a red. */
  readonly enabled: boolean;

  explain(ctx: ExplanationContext): Promise<AIResult<AIExplanation>>;
  reviewWeek(ctx: WeeklyReviewContext): Promise<AIResult<AIWeeklyReview>>;
  analyzeTrend(ctx: TrendContext): Promise<AIResult<AITrendAnalysis>>;
  proposeBlock(ctx: BlockProposalContext): Promise<AIResult<AIBlockProposal>>;
  /**
   * Busca literatura y devuelve fichas candidatas (§8).
   *
   * Es la unica que usa herramientas de servidor —busqueda y descarga web— y
   * por tanto la unica cuyo coste y latencia no se parecen a los demas.
   */
  /*
   * Devuelve el PAYLOAD, no lo que escribió el modelo: lleva además
   * `retrievedUrls`, que añade la Edge Function con lo que sus herramientas
   * descargaron de verdad. El tipo lo dice para que nadie vuelva a validarlo
   * con el esquema corto y se lo quite sin enterarse (22/09/2026).
   */
  reviewEvidence(ctx: EvidenceReviewContext): Promise<AIResult<AIEvidenceReviewPayload>>;
  /**
   * El veredicto FINAL del dominio sobre una propuesta cuya validación termina
   * fuera del proveedor (encargo del 23/09/2026, §3 y §9).
   *
   * En `block_proposal` y `evidence_review`, pasar el esquema y el filtro de
   * prosa NO es aceptar: después vienen el volumen, la estructura, el catálogo
   * y la procedencia. Hasta hoy el proveedor anotaba `accepted = true` al pasar
   * la prosa, y una propuesta que el dominio rechazaba o recortaba después
   * quedaba registrada como aceptada. Ahora queda pendiente hasta que el
   * servicio que la valida llama aquí.
   *
   * Aceptada no es aplicada: aplicar es crear el bloque o guardar las fichas,
   * y eso lo decide quien llama según la política de cambios.
   *
   * Opcional: el proveedor apagado no tiene nada que anotar.
   */
  recordVerdict?(decisionId: string, verdict: { accepted: boolean; reason: string | null }): void;
}

/**
 * Todo lo que se envia a la IA pasa por aqui antes de salir.
 *
 * La implementacion vive en `@cadencia/contracts` (`ai-context-guard.ts`) y se
 * reexporta aqui por comodidad de quien ya importa del dominio. Esta alli
 * porque la MISMA comprobacion tiene que correr en la Edge Function, que es el
 * punto de salida real, y `contracts` es el unico paquete que se copia a
 * `supabase/functions/_shared`. Dos copias de esta regla se separarian, y la
 * que se quedara vieja seguiria pareciendo que protege.
 */
export { auditAIContext, type ContextAudit } from '@cadencia/contracts';

/**
 * Proveedor apagado: el sistema entero funcionando sin IA.
 *
 * No es un doble de test. Es el modo que se usa cuando el usuario apaga la IA,
 * cuando se agota el presupuesto y cuando la red no esta, y es la razon de que
 * CA-7.4 pueda exigir que los seis E2E pasen con la IA desactivada.
 */
export class DisabledCoachAI implements CoachAIProvider {
  readonly enabled = false;

  async explain(): Promise<AIResult<AIExplanation>> {
    return aiFail({ kind: 'disabled' });
  }

  async reviewWeek(): Promise<AIResult<AIWeeklyReview>> {
    return aiFail({ kind: 'disabled' });
  }

  async analyzeTrend(): Promise<AIResult<AITrendAnalysis>> {
    return aiFail({ kind: 'disabled' });
  }


  async reviewEvidence(): Promise<AIResult<AIEvidenceReviewPayload>> {
    return aiFail({ kind: 'disabled' });
  }

  async proposeBlock(): Promise<AIResult<AIBlockProposal>> {
    return aiFail({ kind: 'disabled' });
  }

}

/**
 * Peticiones y su tope de contexto, en tokens (§7.1, CA-7.6).
 *
 * **Ninguno pasa de 4.000, y eso no se negocia**: es el criterio de aceptacion,
 * y `provider.test.ts` lo afirma. Cuando la medicion del 01/09/2026 revelo que
 * el estimador iba corto por mas del doble, la salida no fue subir el tope: fue
 * recortar lo que se envia. `recipe_generation` era el unico que se salia; se
 * retiro con la nutricion el 21/09/2026, y este tope se queda porque
 * arreglo limitando cuantos alimentos se le ofrecen
 * (`RECIPE_CANDIDATE_LIMIT`), no ensanchando la regla que lo detecto.
 */
export const MAX_CONTEXT_TOKENS: Record<AIPurpose, number> = {
  block_proposal: 4000,
  /*
   * La revision de evidencia manda MUY poco: una pregunta y la lista de
   * afirmaciones que se quieren respaldar. Nada del atleta.
   *
   * Y eso no es economia, es el §14: «no envies identidad ni historial personal
   * a buscadores bibliograficos». Lo que esta llamada hace es salir a internet,
   * asi que es la unica del sistema donde el contexto tiene que ser
   * IMPERSONAL por construccion, no solo anonimo. 1.000 sobra y el tope
   * pequeño es en si mismo una guarda: el dia que alguien intente meter aqui
   * el historial del usuario, no cabe.
   */
  evidence_review: 1000,
  trend_analysis: 3000,
  explanation: 1500,
  weekly_review: 3000,
};

/**
 * Caracteres por token que se suponen al estimar.
 *
 * **Dos, y no cuatro.** Cuatro es la regla gruesa que da la documentacion para
 * prosa en ingles, y para lo que aqui se envia —JSON con claves cortas,
 * numeros, guiones y castellano acentuado— se queda corta por mas del doble.
 * Medido el 01/09/2026 contra la API, con la primera llamada real de cada
 * proposito:
 *
 * | Proposito | Estimado con /4 | Entrada medida | Caracteres por token |
 * |---|---|---|---|
 * | `block_proposal` | 299 | 834 | 1,43 |
 *
 * Con /4, un tope de 4.000 dejaba pasar peticiones de casi 9.000 tokens
 * reales. La guarda existia y no guardaba.
 */
const CARACTERES_POR_TOKEN = 2;

/**
 * Estimacion de tokens de un contexto, para no pasarse del tope.
 *
 * Sigue siendo una **aproximacion deliberada**: contar de verdad exige llamar a
 * `count_tokens`, que es una peticion de red, y este numero solo sirve para
 * cortar contextos absurdos antes de salir. El tamano real de cada peticion se
 * registra despues en `ai_requests.inputTokens`, que si viene medido por la
 * API. Lo que cambio el 01/09/2026 no es su naturaleza, es que el numero que
 * usaba estaba mal.
 */
export function estimateContextTokens(context: unknown): number {
  return Math.ceil((JSON.stringify(context) ?? '').length / CARACTERES_POR_TOKEN);
}
